import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { OtpService } from '@app/services/authenticators/otp.service';
import { Button } from '@infocert/ng-ui-kit/models';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-confirm-pin-input',
  templateUrl: './confirm-pin-input.component.html',
  styleUrls: ['./confirm-pin-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ConfirmPinInputComponent),
    },
  ],
})
export class ConfirmPinInputComponent implements ControlValueAccessor, OnInit {
  @Input() labelKey?: string;
  @Input() formControlName: string = '';
  @Input() length: number = 8;
  @Input() submitOnEnter = true;
  @Input() hideValue = true;
  @Input() resetGroup: string = 'contract-pin';
  @Input() error: boolean = false;
  @ViewChild('ngUiKitOtp') ngUiKitOtpRef: any;
  @Output() onUserInput: EventEmitter<void> = new EventEmitter<void>();

  innerCtrl = new FormControl<string>('', { nonNullable: true });
  refreshOtp = true;
  private otpResetSubscription?: Subscription;

  get pinLength(): number {
    return this.length || 8;
  }

  get resendBtnConfig(): Button | undefined {
    return undefined;
  }

  get resolvedLabel(): string {
    return this.labelKey
      ? this.translateService.instant(this.labelKey)
      : '';
  }

  value = '';
  touched = false;
  disabled = false;
  private initialized = false;
  private isResetting = false;
  private ignoreEmptyEmit = true;

  constructor(
    private otpService: OtpService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.otpResetSubscription = this.otpService
      .getEventSubject()
      .subscribe((resetType: any) => {
        if (
          resetType === 'all' ||
          resetType === true ||
          resetType === this.resetGroup
        ) {
          this.reset();
        }
      });

    setTimeout(() => {
      this.initialized = true;
    }, 100);
  }

  onChange = (value: string) => {
    if (this.isResetting) {
      return;
    }
    const safeValue = value ?? '';
    const valueChanged = safeValue !== this.value;
    if (valueChanged) {
      this.onUserInput.emit();
    }

    if (this.ignoreEmptyEmit && !safeValue) {
      return;
    }
    if (safeValue) {
      this.ignoreEmptyEmit = false;
    }

    if (safeValue !== this.innerCtrl.value) {
      this.innerCtrl.setValue(safeValue, { emitEvent: false });
    }

    if (!this.initialized && safeValue.length === 0) {
      return;
    }

    this.value = safeValue;

    this.onChangeCallback(safeValue);
    this.onTouchedCallback();
  };

  onTouched = () => {};

  private onChangeCallback = (value: string) => {};
  private onTouchedCallback = () => {};

  writeValue(val: string | null) {
    const safeValue = val ?? '';
    this.value = safeValue;
    this.innerCtrl.setValue(safeValue, { emitEvent: false });
    if (this.ngUiKitOtpRef?.writeValue) {
      this.ngUiKitOtpRef.writeValue(safeValue);
    }
  }

  registerOnChange(onChange: any) {
    this.onChangeCallback = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouchedCallback = onTouched;
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (disabled) {
      if (!this.innerCtrl.disabled) {
        this.innerCtrl.disable({ emitEvent: false });
      }
    } else if (this.innerCtrl.disabled) {
      this.innerCtrl.enable({ emitEvent: false });
    }
  }

  onEnter(event: Event) {
    if (!this.submitOnEnter) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  reset() {
    this.isResetting = true;
    this.ignoreEmptyEmit = true;
    this.value = '';
    this.innerCtrl.setValue('', { emitEvent: false });
    this.innerCtrl.markAsPristine();
    this.innerCtrl.markAsUntouched();
    this.innerCtrl.updateValueAndValidity({ emitEvent: false });

    if (this.ngUiKitOtpRef?.writeValue) {
      this.ngUiKitOtpRef.writeValue('');
    }

    this.refreshOtp = false;
    this.cdr.detectChanges();
    this.refreshOtp = true;
    this.cdr.detectChanges();

    this.touched = false;
    this.isResetting = false;
  }

  ngOnDestroy(): void {
    this.otpResetSubscription?.unsubscribe();
  }
}
